// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { CustomEvent } from '@shared/utils/CustomEvents';
import { Point, Rectangle } from '@shared/commons/Geometry';
import { KeyCode, KeyEventProcessor } from '@shared/commons/KeyEventProcessor';
import { Scroll } from '../Scroll';
import { ThemeManager } from '../../misc/ThemeManager';
import { ScrollUtils } from '../../UtilsClasses/ScrollUtils';
import { InstrumentLookupQuickTreeNode } from './InstrumentLookupQuickTreeNode';
import { SolidBrush } from '@shared/commons/Graphics';
import { Cursors } from '@shared/commons/Cursors';
import { QuickTableUtils } from '../QuickTable/QuickTableUtils';
export class QuickTree {
    get backgroundColor() {
        if (isValidString(this._backgroundColor)) {
            return this._backgroundColor;
        }
        else {
            return this.isAttached
                ? ThemeManager.colors.attachedQuickTreeBackground
                : ThemeManager.colors.detachedQuickTreeBackground;
        }
    }
    set backgroundColor(value) {
        this._backgroundColor = value;
    }
    get rowHeigth() {
        return this._rowHeigth;
    }
    set rowHeigth(value) {
        this._rowHeigth = value;
        this.reInitScrollHeigth();
    }
    constructor(context, isAttached = false) {
        this._fontSize = 14;
        this.nodeCollection = [];
        this.selectedNode = null;
        this.lastSelectionNode = null;
        this.plainArray = [];
        this.width = 100;
        this.height = 100;
        this.isAttached = false;
        this.lastMouseLoc = new Point(-1, -1);
        this._rowHeigth = QuickTree.NODE_HEIGHT;
        this.workWithScroll = false;
        this.isMultiSelect = false;
        this.isMultiSelectCheckboxUsing = false;
        this.selectedNodesDict = {};
        this.objNode = {};
        this.boldRootNode = true;
        this.SelectionChanged = new CustomEvent();
        this.EnterPressed = new CustomEvent();
        this.isSmall = false;
        this.currentCursor = Cursors.Default;
        this.DrawingContext = context;
        this.DrawingContext.fillStyle = this.backgroundColor;
        this.DrawingContext.fillRect(0, 0, this.width, this.height);
        this.isAttached = isAttached;
        this.scroll = new Scroll(this.width - Scroll.SCROLL_WIDTH, 0, Scroll.SCROLL_WIDTH, this.height, 0, 0, this.DrawingContext, this.rowHeigth, isAttached, false);
        this.scroll.OnValueChange.Subscribe(this.Draw.bind(this), this);
    }
    setSize(newWidth, newHeight) {
        this.width = newWidth;
        this.height = newHeight;
        this.scroll.setBounds(new Rectangle(this.width - Scroll.SCROLL_WIDTH, 0, Scroll.SCROLL_WIDTH, this.height));
    }
    onResize() {
        this.setSize(this.width, this.height);
        this.Draw(true);
    }
    AddNode(newNode) {
        this.nodeCollection.push(newNode);
        this.Draw(true);
    }
    AddNodeRange(newNodesAraay) {
        this.nodeCollection = this.nodeCollection.concat(newNodesAraay);
        this.Draw(true);
    }
    RemoveNode(index) {
        this.nodeCollection.splice(index, 1);
        this.Draw(true);
    }
    Clear() {
        this.nodeCollection = [];
        const ctx = this.DrawingContext;
        // clear place
        ctx.fillStyle = this.backgroundColor;
        ctx.fillRect(0, 0, this.width, this.height);
        this.SelectionChanged.Raise();
    }
    Draw(needUpdatePlainArray = false) {
        const ctx = this.DrawingContext;
        // clear place
        ctx.fillStyle = this.backgroundColor;
        ctx.fillRect(0, 0, this.width, this.height);
        if (needUpdatePlainArray) {
            this.plainArray = this.createPlainArray();
        }
        const pl = this.plainArray;
        const len = pl.length;
        let curY = 0;
        const maxHeight = this.height;
        // let indexHovered = this.plainArray.indexOf(this.hoveredNode)
        if (needUpdatePlainArray) {
            this.scroll.setScrollElementsCount(len);
        }
        for (let i = this.scroll.scrollIndex; i < len; i++) {
            if (!pl[i].visible) {
                continue;
            }
            const node = pl[i];
            if (node instanceof InstrumentLookupQuickTreeNode) {
                node.Draw(ctx, this.backgroundColor, curY, this.rowHeigth, this.width, node === this.hoveredNode, this.nodeOffset);
            }
            else {
                this.DrawNode(ctx, node, curY);
            }
            curY = curY + node.DisplayRectangle.Height;
            if (maxHeight < curY) {
                break;
            }
        }
        this.scroll.Draw();
    }
    DrawNode(ctx, node, curY) {
        ctx.font = ThemeManager.createFont(this._fontSize);
        let xOffset = node.nodeLevel * QuickTree.Level_PADDING - 12;
        const hasChildNodes = node.HasChildNodes();
        if (hasChildNodes) {
            node.CollapseRectangle.X = xOffset + 4;
            node.CollapseRectangle.Y = curY + (this.rowHeigth - QuickTree.COLLAPSE_ICON_SIZE) / 2;
            node.CollapseRectangle.Width = QuickTree.COLLAPSE_ICON_SIZE;
            node.CollapseRectangle.Height = QuickTree.COLLAPSE_ICON_SIZE;
        }
        else {
            node.CollapseRectangle.Empty();
        }
        xOffset += QuickTree.COLLAPSE_ICON_SIZE;
        if (this.isMultiSelectCheckboxUsing) {
            xOffset += 3;
            node.CheckBoxRectangle.X = xOffset;
            node.CheckBoxRectangle.Y = curY + (this.rowHeigth - QuickTree.CHECKBOX_SIZE) / 2;
            node.CheckBoxRectangle.Width = QuickTree.CHECKBOX_SIZE;
            node.CheckBoxRectangle.Height = QuickTree.CHECKBOX_SIZE;
            xOffset += node.CheckBoxRectangle.Width;
        }
        else {
            node.CheckBoxRectangle.Empty();
        }
        xOffset += 3;
        if (node.collapsedImageDef !== null && node.expandedImageDef !== null) {
            node.ImgRectangle.X = xOffset + 3;
            node.ImgRectangle.Y = curY + (this.rowHeigth - QuickTree.IMG_HEIGHT) / 2;
            node.ImgRectangle.Width = QuickTree.IMG_WIDTH;
            node.ImgRectangle.Height = QuickTree.IMG_HEIGHT;
            const gapBetweenImgAndText = 16;
            xOffset += node.ImgRectangle.Width + gapBetweenImgAndText;
        }
        else {
            node.ImgRectangle.Empty();
        }
        node.TextRectangle.X = xOffset;
        node.TextRectangle.Y = curY;
        node.TextRectangle.Width = this.width - xOffset;
        node.TextRectangle.Height = this.rowHeigth;
        node.DisplayRectangle.X = 0;
        node.DisplayRectangle.Y = curY;
        node.DisplayRectangle.Width = this.width;
        node.DisplayRectangle.Height = this.rowHeigth;
        if (node.selected) {
            const treeSelectionBrush = new SolidBrush(ThemeManager.colors.quickTreeNodeBackgroundSelected);
            ctx.FillRect(treeSelectionBrush, 0, node.DisplayRectangle.Y, this.width, node.DisplayRectangle.Height);
        }
        else if (this.hoveredNode === node) {
            const treeSelectionBrush = new SolidBrush(ThemeManager.colors.quickTreeNodeBackgroundHovered);
            ctx.FillRect(treeSelectionBrush, 0, node.DisplayRectangle.Y, this.width, node.DisplayRectangle.Height);
        }
        if (this.isMultiSelectCheckboxUsing) {
            const btnRectangle = node.CheckBoxRectangle;
            const { backgroundBrush, borderPen, image } = this.getCheckBoxDrawings(node);
            ctx.RoundRectangle(btnRectangle, 2, backgroundBrush, borderPen);
            if (!isNullOrUndefined(image)) {
                ctx.DrawCenteredImage(image, btnRectangle, image.width, image.height);
            }
        }
        if (node.collapsedImageDef !== null && node.expandedImageDef !== null) {
            const img = node.collapsed ? node.getCollapsedImage() : node.getExpandedImage();
            if (!isNullOrUndefined(img)) {
                ctx.DrawCenteredImage(img, node.ImgRectangle);
            }
        }
        if (hasChildNodes) {
            this.drawCollapseIcon(ctx, node, node === this.hoveredNode);
        }
        const isExpanded = hasChildNodes && !node.collapsed;
        ctx.fillStyle = node.getForegroundColor(isExpanded, this.hoveredNode === node);
        ctx.DrawTextInRect(node.nodeText, node.TextRectangle, true, 'left', 'middle');
    }
    drawCollapseIcon(ctx, node, hovered) {
        if (isNullOrUndefined(node)) {
            return;
        }
        let img;
        if (node.collapsed) {
            if (node.selected) {
                img = ThemeManager.NewImages.quickTreeExpandSelected.image;
            }
            else if (hovered) {
                img = ThemeManager.NewImages.quickTreeExpandHovered.image;
            }
            else {
                img = ThemeManager.NewImages.quickTreeExpandDefault.image;
            }
        }
        else {
            if (node.selected) {
                img = ThemeManager.NewImages.quickTreeCollapseSelected.image;
            }
            else if (hovered) {
                img = ThemeManager.NewImages.quickTreeCollapseHovered.image;
            }
            else {
                img = ThemeManager.NewImages.quickTreeCollapseDefault.image;
            }
        }
        if (isNullOrUndefined(img)) {
            return;
        }
        const paddingX = node.CollapseRectangle.X;
        const paddingY = node.CollapseRectangle.Y + (node.CollapseRectangle.Height - img.height) / 2;
        ctx.drawImage(img, paddingX, paddingY);
    }
    createPlainArray() {
        const newPlainArray = [];
        const curLevel = 1;
        const myNodes = this.nodeCollection;
        const len = myNodes.length;
        for (let i = 0; i < len; i++) {
            this.createPlain(myNodes[i], newPlainArray, curLevel);
        }
        return newPlainArray;
    }
    createPlain(curNode, array, level) {
        curNode.nodeLevel = level;
        array.push(curNode);
        if (curNode.collapsed) {
            this.processNode(curNode);
            return;
        }
        level++;
        curNode.hasVisibleChild = false;
        const curNodeNodes = curNode.childNodes;
        const len = curNodeNodes.length;
        for (let i = 0; i < len; i++) {
            this.createPlain(curNodeNodes[i], array, level);
            if (curNodeNodes[i].visible) {
                curNode.hasVisibleChild = true;
            }
        }
        curNode.visible = curNode.hasVisibleChild;
        level--;
    }
    processNode(curNode) {
        const curNodeNodes = curNode.childNodes;
        const len = curNodeNodes.length;
        curNode.hasVisibleChild = false;
        for (let i = 0; i < len; i++) {
            this.processNode(curNodeNodes[i]);
            if (curNodeNodes[i].visible) {
                curNode.hasVisibleChild = true;
            }
        }
    }
    onMouseDown(event) {
        var _a;
        if (this.scroll.containerRectangle.Contains(event.offsetX, event.offsetY)) {
            this.scroll.mouseDown(event);
            this.workWithScroll = true;
            return;
        }
        const curNode = this.findNode(event.offsetX, event.offsetY);
        if (isNullOrUndefined(curNode)) {
            return;
        }
        if (curNode.CollapseRectangle.Contains(event.offsetX, event.offsetY)) {
            curNode.DoCollapse();
            this.Draw(true);
            return;
        }
        if (this.isMultiSelect &&
            KeyEventProcessor.ShiftPressed &&
            !isNullOrUndefined((_a = this.lastSelectionNode) === null || _a === void 0 ? void 0 : _a.parentNode) &&
            !isNullOrUndefined(curNode.parentNode)) {
            // var objNode = {};
            const objNodeTmp = this.addNodeToDictShift(curNode, this.objNode);
            for (let i = 0; i < objNodeTmp.nodes.length; i++) {
                this.addNodeToDict(objNodeTmp.nodes[i], false);
            }
            objNodeTmp.nodes = [];
            let selCurNode = null;
            const indexNode = curNode.parentNode.childNodes.indexOf(curNode);
            const indexLastNode = this.lastSelectionNode.parentNode.childNodes.indexOf(this.lastSelectionNode);
            const minIndex = Math.min(indexNode, indexLastNode);
            const maxIndex = Math.max(indexNode, indexLastNode);
            for (let i = minIndex; i <= maxIndex; i++) {
                // selCurNode = curNode.parentNode.childNodes[i];
                // objNode = this.addNodeToDictShift(selCurNode);
                // this.addNodeToDict(selCurNode, !selCurNode.selected)
                selCurNode = curNode.parentNode.childNodes[i];
                objNodeTmp[i] = selCurNode;
                objNodeTmp.nodes.push(selCurNode);
                this.addNodeToDict(selCurNode, true);
            }
            this.SelectionChanged.Raise();
            this.Draw(true);
            // this.lastSelectionNode = curNode;
            return;
        }
        // TODO. Refactor. "Check mark" selection.
        if (this.isMultiSelect && KeyEventProcessor.ControlPressed) {
            if (Object.keys(this.objNode).length > 0) {
                const k = Object.keys(this.objNode);
                for (let i = 0; i < k.length; i++) {
                    this.clearDict(this.objNode[k[i]]);
                }
                this.objNode = {};
                this.CheckUnCheckAll(false);
            }
            this.addNodeToDict(curNode, !curNode.selected);
            this.SelectionChanged.Raise();
            this.Draw(true);
            this.lastSelectionNode = curNode;
            return;
        }
        if (this.isMultiSelectCheckboxUsing && curNode.CheckBoxRectangle.Contains(event.offsetX, event.offsetY)) {
            curNode.checked = !curNode.checked;
            this.addNodeToDict(curNode, curNode.checked);
            this.Draw(true);
            this.SelectionChanged.Raise();
            return;
        }
        if (Object.keys(this.objNode).length > 0) {
            const k = Object.keys(this.objNode);
            for (let i = 0; i < k.length; i++) {
                this.clearDict(this.objNode[k[i]]);
            }
            this.objNode = {};
            this.CheckUnCheckAll(false);
        }
        // Check mark + node selection.
        this.setSelectedNode(curNode);
        this.lastSelectionNode = curNode;
    }
    findNode(x, y) {
        const pl = this.plainArray;
        const startFind = this.scroll.scrollIndex;
        const endFind = Math.min(pl.length, startFind + this.scroll.getVisibleCount());
        for (let i = startFind; i < endFind; i++) {
            const node = pl[i];
            if (node.DisplayRectangle.Contains(x, y)) {
                return node;
            }
        }
        return null;
    }
    clearDict(dict) {
        const k = Object.keys(dict);
        if (!k.includes('nodes')) {
            return;
        }
        for (let i = 0; i < k.length; i++) {
            if (k[i] === 'nodes') {
                continue;
            }
            this.clearDict(dict[k[i]]);
            if (!Array.isArray(dict[k[i]]) && !Object.keys(dict[k[i]]).includes('nodes')) {
                this.addNodeToDict(dict[k[i]], false);
            }
        }
    }
    onMouseMove(event) {
        this.lastMouseLoc = new Point(event.offsetX, event.offsetY);
        this.updateHoveredNode(event.offsetX, event.offsetY);
        this.Draw();
        // For scroll hovering.
        this.scroll.onMouseMove(event);
        if (this.workWithScroll) {
            // For scroll moving.
            this.scroll.mouseMove(event);
        }
    }
    onMouseUp(event) {
        if (this.workWithScroll) {
            this.workWithScroll = false;
            this.scroll.mouseUp(event);
        }
    }
    onMouseWheel(event) {
        hideTooltip();
        this.scroll.mouseWheel(ScrollUtils.IsScrollUp(event.deltaY));
        this.updateHoveredNode(event.offsetX, event.offsetY);
        this.Draw();
    }
    onDoubleClick(event) {
        const curNode = this.findNode(event.offsetX, event.offsetY);
        if (isNullOrUndefined(curNode)) {
            return false;
        }
        if (!curNode.CollapseRectangle.IsEmpty() && curNode.CollapseRectangle.Contains(event.offsetX, event.offsetY)) {
            return false;
        }
        if (curNode.childNodes.length > 0) {
            curNode.DoCollapse();
            this.Draw(true);
            return false;
        }
        this.selectedNode = curNode;
        this.SelectionChanged.Raise();
        return true;
    }
    onMouseLeave(event) {
        this.hoveredNode = null;
        this.currentCursor = Cursors.Default;
        hideTooltip();
        this.Draw();
    }
    // TODO. Optimize if necessary.
    setSelectedNode(node, skipUnSelect = false) {
        if (isNullOrUndefined(node)) {
            return;
        }
        this.CheckUnCheckAll(false);
        if (!isNullOrUndefined(this.selectedNode)) {
            this.selectedNode.selected = false;
        }
        if (this.isMultiSelect) {
            node.setCheck(true);
            if (!skipUnSelect && !isNullOrUndefined(this.selectedNode)) {
                this.selectedNode.setCheck(false);
                this.addNodeToDict(this.selectedNode, false);
            }
            this.addNodeToDict(node, true);
        }
        node.selected = true;
        this.selectedNode = node;
        this.SelectionChanged.Raise();
        this.Draw(true);
    }
    setCheckedNode(node) {
        node.checked = true;
        this.addNodeToDict(node, node.checked);
        this.SelectionChanged.Raise();
    }
    goToSelectedNode() {
        this.unCollapseAllParents(this.selectedNode);
        this.Draw(true);
        const index = this.plainArray.indexOf(this.selectedNode);
        this.scroll.trailingScroll(index);
    }
    // при true пропускаем те что помечены missAtUnCollapse
    unCollapseAllPosible(considerMissed) {
        const arr = this.nodeCollection;
        const len = arr.length;
        for (let i = 0; i < len; i++) {
            arr[i].collapsed = false;
            this.unCollapseAllPosibleWork(arr[i].childNodes, considerMissed);
        }
        this.Draw(true);
        this.scroll.moveScrollToElement(0);
    }
    unCollapseAllPosibleWork(workArr, considerMissed) {
        const len = workArr.length;
        for (let i = 0; i < len; i++) {
            if ((considerMissed && workArr[i].missAtUnCollapse) || workArr[i].childNodes.length === 0) {
                continue;
            }
            workArr[i].collapsed = false;
            this.unCollapseAllPosibleWork(workArr[i].childNodes, considerMissed);
        }
    }
    CheckUnCheckAll(checkAction) {
        if (this.isMultiSelectCheckboxUsing) {
            return;
        }
        const len = this.nodeCollection.length;
        for (let i = 0; i < len; i++) {
            this.addNodeToDict(this.nodeCollection[i], checkAction);
        }
        this.SelectionChanged.Raise();
        this.Draw();
    }
    CheckUnCheckAllMultiCheckbox(checkAction) {
        const len = this.nodeCollection.length;
        for (let i = 0; i < len; i++) {
            this.nodeCollection[i].checked = checkAction;
            this.addNodeToDict(this.nodeCollection[i], checkAction);
        }
        this.SelectionChanged.Raise();
        this.Draw();
    }
    addNodeToDict(newNode, checkAction) {
        if (isNullOrUndefined(newNode)) {
            return;
        }
        const fullPath = newNode.getFullPath();
        const childCount = newNode.childNodes.length;
        const hasChild = childCount > 0;
        if (!this.isMultiSelectCheckboxUsing) {
            newNode.selected = checkAction;
        }
        if (hasChild) {
            const len = childCount;
            for (let i = 0; i < len; i++) {
                this.addNodeToDict(newNode.childNodes[i], checkAction);
            }
        }
        else {
            if (checkAction) {
                this.selectedNodesDict[fullPath] = newNode;
            }
            else {
                delete this.selectedNodesDict[fullPath];
            }
        }
        // newNode.setCheck(selectedAction);
    }
    raiseOnSelectionChanged() {
        this.SelectionChanged.Raise();
    }
    addNodeToDictShift(curNode, objectNode) {
        if (isNullOrUndefined(curNode)) {
            return;
        }
        // var objectNode = {};
        const curN = this.getParrentForNode(curNode, objectNode, 0);
        curN[curNode.parentNode.childNodes.indexOf(curNode)] = curNode;
        return curN;
    }
    getParrentForNode(node, objNodeTree, level) {
        let index = -1;
        const parent = node.parentNode;
        let obj = null;
        if (parent !== null) {
            obj = this.getParrentForNode(parent, objNodeTree, ++level);
            if (level === 1) {
                return obj;
            }
            index = node.parentNode.childNodes.indexOf(node);
            if (index === -1) {
                return obj;
            }
            obj = this.addToObjNodeTree(obj, index);
        }
        else {
            index = this.nodeCollection.indexOf(node);
            obj = this.addToObjNodeTree(objNodeTree, index);
        }
        return obj;
    }
    addToObjNodeTree(objNodeTree, indexNew) {
        if (isNullOrUndefined(objNodeTree[indexNew])) {
            objNodeTree[indexNew] = {};
        }
        if (isNullOrUndefined(objNodeTree[indexNew].nodes)) {
            objNodeTree[indexNew].nodes = [];
        }
        return objNodeTree[indexNew];
    }
    clearNodeDict() {
        this.selectedNodesDict = {};
    }
    onKeyUp() { }
    onKeyDown() {
        const keyProc = KeyEventProcessor;
        const currentButton = keyProc.currentButton;
        if (currentButton === KeyCode.ENTER) {
            this.EnterPressed.Raise();
            return;
        }
        if (currentButton !== KeyCode.UP &&
            currentButton !== KeyCode.DOWN &&
            currentButton !== KeyCode.LEFT &&
            currentButton !== KeyCode.RIGHT) {
            return;
        }
        if (currentButton === KeyCode.UP || currentButton === KeyCode.DOWN) {
            this.trySelectUpDown(keyProc.currentButton === KeyCode.UP);
        }
        if (currentButton === KeyCode.LEFT || currentButton === KeyCode.RIGHT) {
            this.trySelectLeftRight(keyProc.currentButton === KeyCode.LEFT);
        }
    }
    trySelectUpDown(up) {
        const plainArray = this.plainArray;
        const plainArrLength = plainArray.length;
        if (plainArrLength === 0) {
            return;
        }
        const selectedNode = this.selectedNode;
        const selectedIndex = plainArray.indexOf(selectedNode);
        if ((selectedIndex === 0 && up) || (selectedIndex === plainArrLength - 1 && !up)) {
            return;
        }
        if (up) {
            this.setSelectedNode(plainArray[selectedIndex - 1]);
        }
        else {
            this.setSelectedNode(plainArray[selectedIndex + 1]);
        }
        this.goToSelectedNode();
    }
    trySelectLeftRight(left) {
        const plainArray = this.plainArray;
        const plainArrLength = plainArray.length;
        if (plainArrLength === 0) {
            return;
        }
        const selectedNode = this.selectedNode;
        const parrent = selectedNode.parentNode;
        if (left) {
            if (!selectedNode.collapsed) {
                selectedNode.collapsed = true;
            }
            else if (parrent !== null) {
                this.setSelectedNode(parrent);
                this.goToSelectedNode();
            }
        }
        else {
            if (!selectedNode.collapsed && selectedNode.childNodes.length !== 0) {
                this.setSelectedNode(selectedNode.childNodes[0]);
                this.goToSelectedNode();
            }
            if (selectedNode.collapsed && selectedNode.childNodes.length !== 0) {
                selectedNode.DoCollapse();
            }
        }
        this.Draw(true);
    }
    unCollapseAllParents(node) {
        if (isNullOrUndefined(node)) {
            return;
        }
        let parentNode = node.parentNode;
        while (parentNode !== null) {
            parentNode.collapsed = false;
            parentNode = parentNode.parentNode;
        }
    }
    reInitScrollHeigth() {
        this.scroll.elementHeight = this.rowHeigth;
    }
    getCheckBoxDrawings(node) {
        const isHovered = node.CheckBoxRectangle.Contains(this.lastMouseLoc.X, this.lastMouseLoc.Y);
        const { image, backgroundBrush, borderPen } = QuickTableUtils.getCheckBoxDrawings(node.checked, isHovered, false);
        return { backgroundBrush, borderPen, image };
    }
    updateHoveredNode(x, y) {
        this.hoveredNode = this.findNode(x, y);
        if (isNullOrUndefined(this.hoveredNode)) {
            this.currentCursor = Cursors.Default;
            hideTooltip();
        }
        else {
            this.currentCursor = Cursors.Hand;
            const tooltip = this.hoveredNode.getTooltip(this.DrawingContext);
            const rectForTooltip = this.hoveredNode.DisplayRectangle.copy();
            this.showTooltip(tooltip, rectForTooltip);
        }
    }
}
QuickTree.NODE_HEIGHT = 32;
QuickTree.Level_PADDING = 16;
QuickTree.COLLAPSE_ICON_SIZE = 16;
QuickTree.CHECKBOX_SIZE = 16;
QuickTree.IMG_WIDTH = 16;
QuickTree.IMG_HEIGHT = 16;
